import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { useConfig } from "@/lib/useConfig";
import { mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { categoryLabel } from "@/lib/categories";
import { FreeDeliveryProgress } from "@/components/CartExtras";
import { DI, dsvg } from "./desktopUi";

const TRASH = dsvg('<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/>', 17, 1.5);

/** Opens the side cart from anywhere on the desktop site. */
export const openCartDrawer = () => window.dispatchEvent(new Event("kms:cart-open"));

/** Side cart of the desktop site: quick look at the order without leaving the page. */
export function CartDrawer() {
  const { t, locale } = useI18n();
  const { route, navigate } = useRouter();
  const { products } = useCatalog();
  const { delivery } = useConfig();
  const { linesWithProducts, bundleLinesWithData, subtotalUzs, totalItems, updateQuantity, removeItem, updateBundleQuantity, addItem } = useCart();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("kms:cart-open", show);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("kms:cart-open", show);
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  useEffect(() => setOpen(false), [route]);
  useEffect(() => {
    document.documentElement.classList.toggle("dk-noscroll", open);
  }, [open]);

  const go = (name: "cart" | "checkout") => {
    setOpen(false);
    navigate({ name });
  };
  const inCart = new Set(linesWithProducts.map((l) => l.product.id));
  const addons = products.filter((p) => p.orderMode === "buy_now" && !inCart.has(p.id) && (p.variants[0]?.stock ?? 0) > 0).slice(0, 3);
  const empty = linesWithProducts.length === 0 && bundleLinesWithData.length === 0;

  return (
    <div className="cd-root" data-open={open} aria-hidden={!open}>
      <div className="cd-shade" onClick={() => setOpen(false)} />
      <aside className="cd" role="dialog" aria-modal="true" aria-label={t("cart.title")}>
        <header className="cd-h">
          <h2 className="dk-serif">{t("cart.title")}{totalItems > 0 && <small> · {totalItems}</small>}</h2>
          <button onClick={() => setOpen(false)} aria-label={t("common.close")}>×</button>
        </header>
        {empty ? (
          <div className="cd-empty">
            <p>{t("cart.emptyHint")}</p>
            <button className="dk-btn" onClick={() => { setOpen(false); navigate({ name: "catalog" }); }}>{t("cart.goToCatalog")} {DI.arrow}</button>
          </div>
        ) : (
          <>
            <div className="cd-lines">
              {linesWithProducts.map((l) => (
                <div key={`${l.productId}-${l.variantId}`} className="cd-line">
                  <button className="cd-line__img" onClick={() => { setOpen(false); navigate({ name: "product", slug: l.product.slug }); }}>
                    {l.product.images[0] && <img src={mediaUrl(l.product.images[0], 200)} alt="" />}
                  </button>
                  <div className="cd-line__info">
                    <b>{l.product.name}</b>
                    <small>{categoryLabel(l.product, t)} · {l.variant.label}</small>
                    <div className="cd-line__row">
                      <span className="cd-qty">
                        <button onClick={() => (l.quantity > 1 ? updateQuantity(l.productId, l.variantId, l.quantity - 1) : removeItem(l.productId, l.variantId))} aria-label="−">−</button>
                        <b>{l.quantity}</b>
                        <button onClick={() => updateQuantity(l.productId, l.variantId, Math.min(l.variant.stock || 99, l.quantity + 1))} disabled={l.quantity >= (l.variant.stock || 99)} aria-label="+">+</button>
                      </span>
                      <em>{formatUzs(l.lineTotalUzs, locale)}</em>
                    </div>
                  </div>
                  <button className="cd-line__del" onClick={() => removeItem(l.productId, l.variantId)} aria-label={t("desktop.remove")}>{TRASH}</button>
                </div>
              ))}
              {bundleLinesWithData.map((b) => (
                <div key={b.bundleId} className="cd-line">
                  <span className="cd-line__img">{(b.bundle.image || b.bundle.items[0]?.image) && <img src={mediaUrl(b.bundle.image || b.bundle.items[0]?.image || "", 200)} alt="" />}</span>
                  <div className="cd-line__info">
                    <b>{b.bundle.title}</b>
                    <small>{t("desktop.bundle")}</small>
                    <div className="cd-line__row">
                      <span className="cd-qty">
                        <button onClick={() => updateBundleQuantity(b.bundleId, Math.max(0, b.quantity - 1))} aria-label="−">−</button>
                        <b>{b.quantity}</b>
                        <button onClick={() => updateBundleQuantity(b.bundleId, Math.min(Math.max(b.quantity, b.bundle.available), b.quantity + 1))} aria-label="+">+</button>
                      </span>
                      <em>{formatUzs(b.lineTotalUzs, locale)}</em>
                    </div>
                  </div>
                  <button className="cd-line__del" onClick={() => updateBundleQuantity(b.bundleId, 0)} aria-label={t("desktop.remove")}>{TRASH}</button>
                </div>
              ))}
              {addons.length > 0 && (
                <div className="cd-addons">
                  <div className="cd-addons__t">{t("desktop.addMore")}</div>
                  {addons.map((p) => (
                    <div key={p.id} className="cd-addon">
                      {p.images[0] && <img src={mediaUrl(p.images[0], 200)} alt="" />}
                      <span><b>{p.name}</b><small>{formatUzs(p.variants[0]?.priceUzs ?? 0, locale)}</small></span>
                      <button onClick={() => p.variants[0] && addItem(p.id, p.variants[0].id, 1)} aria-label={t("product.addToCart")}>+</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <footer className="cd-f">
              <FreeDeliveryProgress subtotal={subtotalUzs} threshold={delivery.freeThresholdUzs} />
              <div className="cd-total"><span>{t("desktop.goods", { count: totalItems })}</span><b className="dk-serif">{formatUzs(subtotalUzs, locale)}</b></div>
              <button className="dk-btn cd-go" onClick={() => go("checkout")}>{t("desktop.toCheckout")} {DI.arrow}</button>
              <button className="cd-link" onClick={() => go("cart")}>{t("desktop.openCart")}</button>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}
